/* global document */
import React, {
  useEffect, useState, useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss/lib/injectSheet';
import cn from 'classnames';
import { usePopper } from 'react-popper';

import theme from '../../styles/theme';
import { TooltipPlacement, TooltipPointerSize } from './constants';
import isInteractiveElement from '../../helpers/isInteractiveElement';
import CrossIcon from '../../icons/CrossIcon';

const Tooltip = ({
  id,
  classes,
  containerClass,
  triggerClass,
  tooltipClass,
  arrowClass,
  content,
  children,
  placement,
  offset,
  showCloseButton,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const [triggerElement, setTriggerElement] = useState(null);
  const [tooltipElement, setTooltipElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);

  const popperOptions = useMemo(() => {
    const distance = (TooltipPointerSize / 2) + offset;
    return {
      placement,
      modifiers: [
        {
          name: 'offset',
          options: { offset: [0, distance] },
        },
        { name: 'preventOverflow', options: { padding: 10 } },
        {
          name: 'flip',
          options: { fallbackPlacements: ['top', 'bottom', 'left', 'right'] },
        },
        { name: 'arrow', options: { element: arrowElement, padding: 0 } },
      ],
    };
  }, [placement, offset, arrowElement]);

  const { styles, attributes } = usePopper(triggerElement, tooltipElement, popperOptions);

  const toggleTooltip = useCallback(
    () => {
      setIsVisible(prev => !prev);
    },
    [],
  );

  const handleKeyDown = useCallback(e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleTooltip();
    }
  }, [toggleTooltip]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const handleClickOutside = event => {
      if (
        triggerElement
        && tooltipElement
        && !triggerElement.contains(event.target)
        && !tooltipElement.contains(event.target)
      ) {
        toggleTooltip();
      }
    };

    const handleEscapeKey = event => {
      if (event.key === 'Escape') {
        toggleTooltip();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isVisible, triggerElement, tooltipElement, toggleTooltip]);

  const trigger = useMemo(
    () => {
      const isChildrenInteractive = isInteractiveElement(children);
      const triggerProps = {
        id: `${id}-tooltip-trigger`,
        ref: setTriggerElement,
        className: cn(classes.trigger, triggerClass),
        'aria-expanded': isVisible,
        'aria-describedby': isVisible ? `${id}-tooltip-tooltip` : undefined,
        onClick: toggleTooltip,
        onKeyDown: handleKeyDown,
      };

      if (isChildrenInteractive) {
        return React.cloneElement(children, {
          ...triggerProps,
          onClick: e => {
            toggleTooltip();
            if (children.props.onClick) {
              children.props.onClick(e);
            }
          },
          onKeyDown: e => {
            handleKeyDown(e);
            if (children.props.onKeyDown) {
              children.props.onKeyDown(e);
            }
          },
          className: cn(children.props.className, classes.trigger, triggerClass),
        });
      }

      return (
        <div
          role="button"
          tabIndex={0}
          {...triggerProps}
        >
          {children}
        </div>
      );
    },
    [children, id, classes.trigger, triggerClass, isVisible, toggleTooltip, handleKeyDown],
  );

  const tooltip = useMemo(
    () => {
      if (!isVisible) {
        return null;
      }

      return (
        <div
          id={`${id}-tooltip-tooltip`}
          ref={setTooltipElement}
          role="tooltip"
          className={cn(classes.tooltip, tooltipClass)}
          style={styles.popper}
          {...attributes.popper}
        >
          {showCloseButton && (
            <button
              type="button"
              className={classes.closeButton}
              onClick={toggleTooltip}
              aria-label="Close tooltip"
            >
              <CrossIcon />
            </button>
          )}

          {content}

          <div
            ref={setArrowElement}
            style={{
              ...styles.arrow,
              transform:
                `${styles.arrow?.transform || ''} rotate(45deg)`.trim(),
            }}
            {...attributes.arrow}
            className={cn(classes.arrow, arrowClass)}
          />
        </div>
      );
    },
    [
      isVisible, styles, attributes, id, classes.tooltip, tooltipClass,
      classes.arrow, arrowClass, content,
    ],
  );

  return (
    <div
      className={cn(classes.container, containerClass)}
      id={`${id}-tooltip-container`}
      data-popper-placement={attributes.popper?.['data-popper-placement']}
    >
      {trigger}
      {tooltip}
    </div>
  );
};

Tooltip.propTypes = {
  id: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  containerClass: PropTypes.string,
  triggerClass: PropTypes.string,
  tooltipClass: PropTypes.string,
  arrowClass: PropTypes.string,
  content: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(Object.values(TooltipPlacement)),
  offset: PropTypes.number,
  showCloseButton: PropTypes.bool,
};

Tooltip.defaultProps = {
  id: 'tooltip',
  containerClass: null,
  triggerClass: null,
  tooltipClass: null,
  arrowClass: null,
  placement: TooltipPlacement.BOTTOM,
  offset: 8,
  showCloseButton: false,
};

/**
 * @type {Object.<string, React.CSSProperties | Object.<string, React.CSSProperties>>}
 */
const styles = {
  container: {
    '&[data-popper-placement^="top"] $arrow': {
      bottom: -4,
    },
    '&[data-popper-placement^="bottom"] $arrow': {
      top: -4,
    },
    '&[data-popper-placement^="left"] $arrow': {
      right: -4,
    },
    '&[data-popper-placement^="right"] $arrow': {
      left: -4,
    },
  },
  trigger: {
    cursor: 'pointer',
  },
  tooltip: {
    backgroundColor: theme.colors.black,
    padding: 12,
    borderRadius: 4,
    zIndex: 1000,
    maxWidth: 'calc(100% - 16px)',
    color: theme.colors.white,
    fontFamily: theme.fonts.body,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.5,
    boxSizing: 'border-box',
    position: 'relative',

    [theme.breakpoints.up('md')]: {
      maxWidth: 384,
    },
  },
  closeButton: {
    '&&': {
      position: 'absolute',
      top: 4,
      right: 4,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 4,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.colors.white,

      '&&:hover': {
        opacity: 0.8,
      },

      '& svg': {
        width: 16,
        height: 16,
      },
    },
  },
  arrow: {
    position: 'absolute',
    width: TooltipPointerSize,
    height: TooltipPointerSize,
    background: 'inherit',
    transform: 'rotate(45deg)',
  },
};

export default injectSheet(styles)(Tooltip);
